import React from 'react';
import _ from 'lodash';

import Money from 'components/Money';
import BooleanIcon from 'components/BooleanIcon';
import NotesCount from 'components/NotesCount';
import NullBooleanIcon from 'components/NullBooleanIcon';
import KycSectionStatusBox from 'components/KycSectionStatusBox';

import {
  KycStatusBox,
  RentPlanStatusBox,
  AffordabilityStatusBox
} from './components';
import DepositSummaryStatusTag from 'components/DepositSummaryStatusTag';
import {
  default as RoomCheckStatusTag,
  PENDING as PENDING_ROOM_CHECK_STATUS
} from 'components/RoomCheckStatusTag';
import StatusBox from 'components/StatusBox';
import AgreementStatus from 'components/AgreementStatus';

import {
  humanReadableGCStatusForBookingTracker,
  GOCARDLESS_STATUSES_FOR_BOOKING_TRACKER
} from 'ducks/tenancy';

import './styles.css';

const ScrollableTableSubRow = ({tenant}) => (
  <div className="ScrollableTableSubRow colab-centered-v">
    <div className="ScrollableTableSubRow__order colab-centered">-</div>
    <div className="ScrollableTableSubRow__roomType colab-centered-v">-</div>
    <div className="ScrollableTableSubRow__room colab-centered-v">-</div>
    <div className="ScrollableTableSubRow__allocatedBy colab-centered-v">-</div>
    <div className="ScrollableTableSubRow__kyc colab-centered-v">
      <KycStatusBox kycBooleanStatus={tenant.kycPassed} />
    </div>
    <div className="ScrollableTableSubRow__id colab-centered-v">
      <KycSectionStatusBox status={tenant.kycPoidStatus} />
    </div>
    <div className="ScrollableTableSubRow__visa colab-centered-v">
      <KycSectionStatusBox status={tenant.kycVisaStatus} />
    </div>
    <div className="ScrollableTableSubRow__proofOfAddress colab-centered-v">
      <KycSectionStatusBox status={tenant.kycPoaStatus} />
    </div>
    <div className="ScrollableTableSubRow__affordability colab-centered-v">
      -
    </div>
    <div className="ScrollableTableSubRow__rentPlan colab-centered-v">-</div>
    <div className="ScrollableTableSubRow__agreement colab-centered-v">
      <NullBooleanIcon value={tenant.hasSignedAgreement} />
    </div>
    <div className="ScrollableTableSubRow__balance colab-centered-v">-</div>
    <div className="ScrollableTableSubRow__firstRentPaid colab-centered-v">
      -
    </div>
    <div className="ScrollableTableSubRow__goCardless colab-centered-v">-</div>
    <div className="ScrollableTableSubRow__deposit colab-centered-v">-</div>
    <div className="ScrollableTableSubRow__arrivalRoomCheck colab-centered-v">
      -
    </div>
    <div className="ScrollableTableSubRow__departureRoomCheck colab-centered-v">
      -
    </div>
    <div className="ScrollableTableSubRow__verificationMethod colab-centered-v">
      -
    </div>
    <div className="ScrollableTableSubRow__notes colab-centered-v">-</div>
  </div>
);

const ScrollableBookingRow = ({
  booking,
  hasAgreement,
  goCardlessStatus,
  goCardlessStatusBoxColor,
  openedBookings,
  openBookingNotesModal
}) => {
  return (
    <React.Fragment>
      <div className="ScrollableBookingRow colab-centered-v" key={booking.id}>
        <div className="ScrollableBookingRow__order colab-centered">
          {booking.order ? 'Renewal' : 'New'}
        </div>
        <div className="ScrollableBookingRow__roomType">{booking.roomType}</div>
        <div className="ScrollableBookingRow__room">{booking.room}</div>
        <div className="ScrollableBookingRow__allocatedBy">
          {booking.allocatedBy}
        </div>
        <div className="ScrollableBookingRow__kyc">
          <KycStatusBox kycBooleanStatus={booking.kycPassed} />
        </div>
        <div className="ScrollableBookingRow__id">-</div>
        <div className="ScrollableBookingRow__visa">-</div>
        <div className="ScrollableBookingRow__proofOfAddress">-</div>
        <div className="ScrollableBookingRow__affordability">
          <AffordabilityStatusBox
            affordabilityPassed={booking.affordabilityPassed}
          />
        </div>
        <div className="ScrollableBookingRow__rentPlan">
          <RentPlanStatusBox hasRentSchedule={booking.hasRentSchedule} />
        </div>
        <div className="ScrollableBookingRow__agreement">
          {hasAgreement && <AgreementStatus status={booking.agreementStatus} />}
        </div>
        <div className="ScrollableBookingRow__balance">
          <Money balance amount={booking.bookingSetBalance} />
        </div>
        <div className="ScrollableBookingRow__firstRentPaid">
          <BooleanIcon value={booking.hasPaidRentInvoice} />
        </div>
        <div className="ScrollableBookingRow__goCardless">
          <StatusBox
            status={humanReadableGCStatusForBookingTracker(goCardlessStatus)}
            color={goCardlessStatusBoxColor}
          />
        </div>
        <div className="ScrollableBookingRow__deposit">
          {booking.depositStatus && (
            <DepositSummaryStatusTag status={booking.depositStatus} />
          )}
        </div>
        <div className="ScrollableBookingRow__arrivalRoomCheck">
          {!booking.arrivalRoomCheckStatus && (
            <RoomCheckStatusTag status={PENDING_ROOM_CHECK_STATUS} />
          )}
          {booking.arrivalRoomCheckStatus && (
            <RoomCheckStatusTag status={booking.arrivalRoomCheckStatus} />
          )}
        </div>
        <div className="ScrollableBookingRow__departureRoomCheck">
          {!booking.departureRoomCheckStatus && (
            <RoomCheckStatusTag status={PENDING_ROOM_CHECK_STATUS} />
          )}
          {booking.departureRoomCheckStatus && (
            <RoomCheckStatusTag status={booking.departureRoomCheckStatus} />
          )}
        </div>
        <div className="ScrollableBookingRow__verificationMethod">
          {booking.verificationMethod}
        </div>
        <div className="ScrollableBookingRow__notes">
          <NotesCount
            notesCount={booking.notesCount}
            showNotification={booking.hasUnresolvedNotes}
            onClick={() =>
              openBookingNotesModal({
                bookingId: booking.id
              })
            }
          />
        </div>
      </div>
      {_.includes(openedBookings, booking.id) &&
        !_.isEmpty(booking.primaryTenant) && (
          <ScrollableTableSubRow tenant={booking.primaryTenant} />
        )}
      {_.includes(openedBookings, booking.id) &&
        _.get(booking, 'secondaryTenant.fullName') && (
          <ScrollableTableSubRow tenant={booking.secondaryTenant} />
        )}
      {_.includes(openedBookings, booking.id) &&
        _.get(booking, 'guarantor.fullName') && (
          <ScrollableTableSubRow tenant={booking.guarantor} />
        )}
    </React.Fragment>
  );
};

interface BookingsForMonthProps {
  bookings: any;
  openBookingNotesModal: any;
  openedBookings: Array<number>;
}

class ScrollableBookingsForMonth extends React.Component<
  BookingsForMonthProps
> {
  render() {
    const {bookings, openedBookings, openBookingNotesModal} = this.props;

    return (
      <div className="ScrollableBookingsForMonth">
        <div className="ScrollableBookingsForMonth__header" />
        <div className="ScrollableBookingsForMonth__content">
          {_.map(bookings, booking => {
            const hasAgreement = booking.agreementStatus != 'N/A';
            const goCardlessStatus = booking.invitationStatus;
            const goCardlessStatusBoxColor =
              GOCARDLESS_STATUSES_FOR_BOOKING_TRACKER[goCardlessStatus].color;
            return (
              <ScrollableBookingRow
                key={booking.id}
                booking={booking}
                hasAgreement={hasAgreement}
                goCardlessStatus={goCardlessStatus}
                goCardlessStatusBoxColor={goCardlessStatusBoxColor}
                openedBookings={openedBookings}
                openBookingNotesModal={openBookingNotesModal}
              />
            );
          })}
        </div>
      </div>
    );
  }
}

export default ScrollableBookingsForMonth;
